import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  formatPostFlightMoney,
  formatPostFlightPctBetter,
  type PostFlightOpportunityMetrics,
} from "../../../lib/post-flight-opportunity-metrics.js";

interface MetricChip {
  key: string;
  label: string;
  value: string;
  tip: string;
}

/** Compact labelled figures for a winning ad. Empty when none of the metrics were stored. */
export function PostFlightMetricChips({
  metrics,
  className,
}: {
  metrics: PostFlightOpportunityMetrics;
  className?: string;
}) {
  const { t } = useTranslation();
  const trendLabel =
    metrics.trend === "improving"
      ? t("personaOpportunities.postFlightOpportunity.metrics.trendImproving", "Improving")
      : metrics.trend === "worsening"
        ? t("personaOpportunities.postFlightOpportunity.metrics.trendWorsening", "Worsening")
        : metrics.trend === "flat"
          ? t("personaOpportunities.postFlightOpportunity.metrics.trendFlat", "Flat")
          : null;

  const chips: MetricChip[] = [
    metrics.cpa != null
      ? {
          key: "cpa",
          label: t("personaOpportunities.postFlightOpportunity.metrics.cpa", "CPA"),
          value: formatPostFlightMoney(metrics.cpa),
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.cpaTooltip",
            "Cost per acquisition on this winning ad.",
          ),
        }
      : null,
    metrics.accountCpa != null
      ? {
          key: "accountCpa",
          label: t("personaOpportunities.postFlightOpportunity.metrics.accountCpa", "Account CPA"),
          value: formatPostFlightMoney(metrics.accountCpa),
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.accountCpaTooltip",
            "The account's CPA over the same window.",
          ),
        }
      : null,
    metrics.pctBetter != null
      ? {
          key: "pctBetter",
          label: t("personaOpportunities.postFlightOpportunity.metrics.pctBetter", "% better"),
          value: formatPostFlightPctBetter(metrics.pctBetter),
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.pctBetterTooltip",
            "How much better this ad's KPI is than the account baseline.",
          ),
        }
      : null,
    metrics.purchases != null
      ? {
          key: "purchases",
          label: t("personaOpportunities.postFlightOpportunity.metrics.purchases", "Purchases"),
          value: Math.round(metrics.purchases).toLocaleString("en-US"),
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.purchasesTooltip",
            "Purchases attributed to this ad.",
          ),
        }
      : null,
    metrics.spend != null
      ? {
          key: "spend",
          label: t("personaOpportunities.postFlightOpportunity.metrics.spend", "Spend"),
          value: formatPostFlightMoney(metrics.spend),
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.spendTooltip",
            "Spend on this winning ad.",
          ),
        }
      : null,
    metrics.adRank != null
      ? {
          key: "rank",
          label: t("personaOpportunities.postFlightOpportunity.metrics.rank", "Rank"),
          value: `#${Math.round(metrics.adRank)}`,
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.rankTooltip",
            "Rank of this ad in the post-flight window.",
          ),
        }
      : null,
    trendLabel
      ? {
          key: "trend",
          label: t("personaOpportunities.postFlightOpportunity.metrics.trend", "Trend"),
          value: trendLabel,
          tip: t(
            "personaOpportunities.postFlightOpportunity.metrics.trendTooltip",
            "CPA in the second half of the window compared with the first. Lower CPA is better.",
          ),
        }
      : null,
  ].filter((chip): chip is MetricChip => chip != null);

  if (chips.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn("flex flex-wrap gap-1.5", className)}>
        {chips.map((chip) => (
          <Tooltip key={chip.key}>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px]">
                <span className="text-muted-foreground">{chip.label}</span>
                <span className="font-medium tabular-nums text-foreground">{chip.value}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>{chip.tip}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
